import { maximumBundleDraftInstances } from "./bundle";

export const goal2AggregateMetricVersion = "consumer-lifecycle-v1" as const;

export const goal2AggregateReports = [
  "bundle-adoption",
  "bundle-selection",
  "draft-lifecycle",
  "completion-activity",
  "continuation-adoption",
  "bundle-save-quality",
  "delivery-outcomes",
  "recovery-quality",
] as const;
export type Goal2AggregateReport = (typeof goal2AggregateReports)[number];

export const goal2AggregateBreakdowns = [
  "none",
  "market",
  "broad-category",
  "subcategory",
  "service-type",
  "bundle-version",
  "origin",
  "platform",
  "application-release",
] as const;
export type Goal2AggregateBreakdown = (typeof goal2AggregateBreakdowns)[number];

export const goal2SelectionSizeBands = ["1", "2-3", "4-6", "7-10", "11-20", "21-32"] as const;
export type Goal2SelectionSizeBand = (typeof goal2SelectionSizeBands)[number];

export type Goal2AggregateCellState =
  | Readonly<{
      state: "value";
      key: string;
      value: number;
      denominator: number | null;
      independentScopes: number;
    }>
  | Readonly<{
      state: "not-enough";
      key: string;
      message: "Not enough aggregate activity to report";
    }>
  | Readonly<{ state: "not-collected"; key: string; message: "Not collected" }>
  | Readonly<{ state: "incomplete"; key: string; message: "Incomplete" }>;

export type Goal2AggregateCellInput = Readonly<{
  key: string;
  value: number;
  denominator: number | null;
  independentScopes: number;
  sensitivity: "ordinary" | "broad-sensitive" | "sensitive-leaf";
  complementGroup: string | null;
}>;

const goal2LatencyBands = [
  { key: "latency/under-1-second", label: "under 1 second" },
  { key: "latency/1-to-3-seconds", label: "1 to 3 seconds" },
  { key: "latency/3-to-10-seconds", label: "3 to 10 seconds" },
  { key: "latency/10-to-30-seconds", label: "10 to 30 seconds" },
  { key: "latency/30-seconds-or-more", label: "30 seconds or more" },
] as const;

export type Goal2LatencySummary =
  | Readonly<{
      state: "value";
      sampleCount: number;
      approximateP50: (typeof goal2LatencyBands)[number]["label"];
      approximateP95: (typeof goal2LatencyBands)[number]["label"];
    }>
  | Readonly<{ state: "unavailable"; message: string }>;

function wholeNonNegative(value: number, label: string) {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`Use a valid ${label}.`);
  return value;
}

export function goal2SelectionSizeBand(memberCount: number): Goal2SelectionSizeBand {
  const count = wholeNonNegative(memberCount, "bundle member count");
  if (count < 1 || count > maximumBundleDraftInstances)
    throw new Error(`Use a bundle size between 1 and ${maximumBundleDraftInstances}.`);
  if (count === 1) return "1";
  if (count <= 3) return "2-3";
  if (count <= 6) return "4-6";
  if (count <= 10) return "7-10";
  if (count <= 20) return "11-20";
  return "21-32";
}

export function goal2TechnicalReliability(
  input: Readonly<{
    technicalSuccesses: number;
    technicalFailures: number;
    validationRefusals: number;
    authorizationRefusals: number;
    staleVersionRefusals: number;
    unresolved: number;
  }>,
): Readonly<{
  numerator: number;
  denominator: number;
  ratio: number | null;
  excludedRefusals: number;
  unresolved: number;
}> {
  const technicalSuccesses = wholeNonNegative(input.technicalSuccesses, "success count");
  const technicalFailures = wholeNonNegative(input.technicalFailures, "failure count");
  const excludedRefusals =
    wholeNonNegative(input.validationRefusals, "validation-refusal count") +
    wholeNonNegative(input.authorizationRefusals, "authorization-refusal count") +
    wholeNonNegative(input.staleVersionRefusals, "stale-version-refusal count");
  const unresolved = wholeNonNegative(input.unresolved, "unresolved count");
  const denominator = technicalSuccesses + technicalFailures;
  return {
    numerator: technicalSuccesses,
    denominator,
    ratio: denominator === 0 ? null : technicalSuccesses / denominator,
    excludedRefusals,
    unresolved,
  };
}

/** Derives bounded approximations from protected fixed bands; it never consumes raw timings. */
export function goal2LatencySummary(
  cells: ReadonlyArray<Goal2AggregateCellState>,
): Goal2LatencySummary {
  const known: ReadonlyMap<string, (typeof goal2LatencyBands)[number]> = new Map(
    goal2LatencyBands.map((band) => [band.key, band] as const),
  );
  const latencyCells = cells.filter((cell) => cell.key.startsWith("latency/"));
  if (latencyCells.some((cell) => !known.has(cell.key)))
    throw new Error("Use only the closed Goal 2 latency bands.");
  if (new Set(latencyCells.map((cell) => cell.key)).size !== latencyCells.length)
    throw new Error("Latency band keys must be unique.");
  const withheld = latencyCells.find((cell) => cell.state !== "value");
  if (withheld !== undefined) return { state: "unavailable", message: withheld.message };
  const counts = new Map<string, number>();
  for (const cell of latencyCells) {
    if (cell.state !== "value") continue;
    counts.set(cell.key, wholeNonNegative(cell.value, "latency sample count"));
  }
  const sampleCount = [...counts.values()].reduce((total, value) => total + value, 0);
  if (!Number.isSafeInteger(sampleCount)) throw new Error("Use a valid latency sample total.");
  if (sampleCount === 0)
    return { state: "unavailable", message: "No protected latency samples were available" };

  const quantile = (fraction: number) => {
    const target = Math.ceil(sampleCount * fraction);
    let cumulative = 0;
    for (const band of goal2LatencyBands) {
      cumulative += counts.get(band.key) ?? 0;
      if (cumulative >= target) return band.label;
    }
    throw new Error("The protected latency histogram is incomplete.");
  };
  return {
    state: "value",
    sampleCount,
    approximateP50: quantile(0.5),
    approximateP95: quantile(0.95),
  };
}

function validateCell(input: Goal2AggregateCellInput) {
  const key = input.key.trim();
  if (key.length < 1 || key.length > 160) throw new Error("Use a closed aggregate cell key.");
  const value = wholeNonNegative(input.value, "aggregate value");
  const denominator =
    input.denominator === null
      ? null
      : wholeNonNegative(input.denominator, "aggregate denominator");
  if (denominator !== null && value > denominator)
    throw new Error("An aggregate numerator cannot exceed its denominator.");
  const independentScopes = wholeNonNegative(input.independentScopes, "independent-scope count");
  const complementGroup = input.complementGroup?.trim() ?? null;
  if (complementGroup !== null && (complementGroup.length < 1 || complementGroup.length > 120))
    throw new Error("Use a closed complementary-suppression group.");
  return { ...input, key, value, denominator, independentScopes, complementGroup };
}

function belowPrivacyFloor(input: ReturnType<typeof validateCell>) {
  if (input.sensitivity === "sensitive-leaf") return true;
  return input.independentScopes < (input.sensitivity === "broad-sensitive" ? 20 : 10);
}

/** Conservative complementary suppression: one hidden leaf hides its whole reported complement. */
export function protectGoal2AggregateCells(
  inputs: ReadonlyArray<Goal2AggregateCellInput>,
  collection: "complete" | "not-collected" | "incomplete",
): ReadonlyArray<Goal2AggregateCellState> {
  const normalized = inputs.map(validateCell);
  if (new Set(normalized.map((cell) => cell.key)).size !== normalized.length)
    throw new Error("Aggregate cell keys must be unique.");
  if (collection !== "complete")
    return normalized.map((cell) =>
      collection === "not-collected"
        ? { state: "not-collected" as const, key: cell.key, message: "Not collected" as const }
        : { state: "incomplete" as const, key: cell.key, message: "Incomplete" as const },
    );

  const suppressedGroups = new Set(
    normalized
      .filter((cell) => belowPrivacyFloor(cell) && cell.complementGroup !== null)
      .map((cell) => cell.complementGroup as string),
  );
  return normalized.map((cell): Goal2AggregateCellState => {
    if (
      belowPrivacyFloor(cell) ||
      (cell.complementGroup !== null && suppressedGroups.has(cell.complementGroup))
    )
      return {
        state: "not-enough",
        key: cell.key,
        message: "Not enough aggregate activity to report",
      };
    return {
      state: "value",
      key: cell.key,
      value: cell.value,
      denominator: cell.denominator,
      independentScopes: cell.independentScopes,
    };
  });
}

export function normalizeGoal2AggregateRequest(
  input: Readonly<{
    report: unknown;
    period: unknown;
    breakdown: unknown;
  }>,
): Readonly<{
  report: Goal2AggregateReport;
  period: string;
  breakdown: Goal2AggregateBreakdown;
}> {
  if (!goal2AggregateReports.includes(input.report as never))
    throw new Error("Choose an available aggregate report.");
  if (typeof input.period !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/u.test(input.period))
    throw new Error("Choose a valid completed UTC month.");
  if (!goal2AggregateBreakdowns.includes(input.breakdown as never))
    throw new Error("Choose one available aggregate breakdown.");
  return {
    report: input.report as Goal2AggregateReport,
    period: input.period,
    breakdown: input.breakdown as Goal2AggregateBreakdown,
  };
}
